import { useState } from 'react';
import { Controller } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { motion } from 'framer-motion';
import { ROUTE_PATHS } from 'src/shared/config/routes';
import { getBrowserTimezone } from 'src/shared/utils/timezone';

import { Alert, Checkbox, Tooltip } from 'src/shared/components/ui';
import { ButtonV2, Input, Select, Typography } from 'src/shared/components/ui';

import { useRegister } from '../hooks/useRegister';
import { useRegisterForm } from '../hooks/useRegisterForm';
import { ThankYouLocationState } from '../hooks/useSignupConversionTracking';
import { openRegisterSchema, RegisterFormValues } from '../model/schema';
import { getSignupAttribution } from '../model/signupAttribution';

const MONTHLY_REVENUE_OPTIONS = [
  { key: 'Less Than 10K', label: 'Less Than 10K' },
  { key: '10K to 50K', label: '10K to 50K' },
  { key: '50K to 100K', label: '50K to 100K' },
  { key: '100K-1M', label: '100K-1M' },
  { key: '1M-5M', label: '1M-5M' },
  { key: '5M+', label: '5M+' }
];
// backend/cavela/models.py > JobTitle
const JOB_TITLE_OPTIONS = [
  { key: 'founder_ceo', label: 'Founder / CEO' },
  { key: 'procurement_sourcing', label: 'Procurement / Sourcing' },
  { key: 'operations', label: 'Operations' },
  { key: 'supply_chain', label: 'Supply Chain' },
  { key: 'product_design', label: 'Product / Design' },
  { key: 'finance', label: 'Finance' },
  { key: 'marketing_sales', label: 'Marketing / Sales' },
  { key: 'other', label: 'Other' }
];
const MAX_REGISTER_EMAIL_LENGTH = 150;
const MAX_REGISTER_NAME_LENGTH = 150;
const MAX_REGISTER_PHONE_LENGTH = 50;
const MAX_REGISTER_COMPANY_NAME_LENGTH = 255;
const MAX_REGISTER_WEBSITE_LENGTH = 2000;

interface RegisterFormProps {
  openSignUp: boolean;
}

const RegisterForm: React.FC<RegisterFormProps> = ({ openSignUp }) => {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    getValues,
    setValue,
    watch
  } = useRegisterForm(openRegisterSchema);
  const registerMutation = useRegister();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState('');
  const agreed_to_terms = watch('agreed_to_terms');

  const onSubmit = (data: RegisterFormValues) => {
    registerMutation.mutate(
      {
        ...data,
        ...getSignupAttribution(searchParams, document.cookie),
        timezone: getBrowserTimezone()
      },
      {
        onSuccess: () => {
          const state: ThankYouLocationState = {
            email: getValues().email,
            openSignUp,
            signupCompleted: true
          };

          navigate(ROUTE_PATHS.REGISTER_THANK_YOU, { state });
        },
        onError: (error) => {
          setError(error.message);
        }
      }
    );
  };

  return (
    <div className="flex flex-col justify-center mt-6">
      <Typography variant="h1" weight="bold">
        Sign up
      </Typography>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-6 mt-6 items-start"
      >
        <Input
          id="register-email-input"
          data-testid="email-input"
          label="Email"
          placeholder="name@company.com"
          errorMessage={errors.email?.message}
          isInvalid={!!errors.email}
          type="text"
          inputMode="email"
          autoComplete="email"
          isRequired
          maxLength={MAX_REGISTER_EMAIL_LENGTH}
          {...register('email')}
        />

        <div className="flex gap-4 w-full">
          <Input
            data-testid="first-name-input"
            label="First name"
            placeholder="First name"
            errorMessage={errors.first_name?.message}
            isInvalid={!!errors.first_name}
            isRequired
            maxLength={MAX_REGISTER_NAME_LENGTH}
            {...register('first_name')}
          />
          <Input
            data-testid="last-name-input"
            label="Last name"
            placeholder="Last name"
            errorMessage={errors.last_name?.message}
            isInvalid={!!errors.last_name}
            isRequired
            maxLength={MAX_REGISTER_NAME_LENGTH}
            {...register('last_name')}
          />
        </div>

        <Input
          id="register-phone-input"
          data-testid="phone-number-input"
          label="Phone number"
          placeholder="+1 (555) 000-0000"
          errorMessage={errors.phone_number?.message}
          isInvalid={!!errors.phone_number}
          type="tel"
          isRequired
          maxLength={MAX_REGISTER_PHONE_LENGTH}
          {...register('phone_number')}
        />

        <Controller
          name="job_title"
          control={control}
          render={({ field }) => (
            <Select
              data-testid="job-title-select"
              label="What is your job title?"
              placeholder="Select a job title"
              items={JOB_TITLE_OPTIONS}
              fullWidth
              selectedKeys={field.value ? new Set([field.value]) : new Set()}
              onSelectionChange={(keys: Set<string>) => {
                field.onChange(Array.from(keys)[0] ?? '');
              }}
              isInvalid={!!errors.job_title}
              errorMessage={errors.job_title?.message}
            />
          )}
        />

        <Input
          data-testid="company-name-input"
          label="Company name"
          placeholder="Yourcompany Inc."
          errorMessage={errors.company_name?.message}
          isInvalid={!!errors.company_name}
          maxLength={MAX_REGISTER_COMPANY_NAME_LENGTH}
          {...register('company_name')}
        />

        <Input
          data-testid="company-website-input"
          label="Company website"
          placeholder="https://yourcompany.com"
          errorMessage={errors.company_website?.message}
          isInvalid={!!errors.company_website}
          maxLength={MAX_REGISTER_WEBSITE_LENGTH}
          {...register('company_website')}
        />

        <Controller
          name="monthly_revenue"
          control={control}
          render={({ field }) => (
            <Select
              data-testid="monthly-revenue-select"
              label="What is your monthly revenue?"
              items={MONTHLY_REVENUE_OPTIONS}
              fullWidth
              isRequired
              selectedKeys={
                field.value
                  ? new Set([
                      MONTHLY_REVENUE_OPTIONS.find(
                        (o) => o.label === field.value
                      )?.key ?? ''
                    ])
                  : new Set()
              }
              onSelectionChange={(keys: Set<string>) => {
                const selectedKey = Array.from(keys)[0] as string;
                const selectedLabel =
                  MONTHLY_REVENUE_OPTIONS.find((o) => o.key === selectedKey)
                    ?.label ?? '';

                field.onChange(selectedLabel);
              }}
              isInvalid={!!errors.monthly_revenue}
              errorMessage={errors.monthly_revenue?.message}
            />
          )}
        />

        <div className="flex items-start">
          <Checkbox
            data-testid="agreed-to-terms-checkbox"
            isSelected={agreed_to_terms}
            onValueChange={(value) => setValue('agreed_to_terms', value)}
          />
          <div
            className="text-sm cursor-pointer"
            onClick={() => setValue('agreed_to_terms', !agreed_to_terms)}
          >
            I agree to the{' '}
            <a
              href="https://cavela.com/tos"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:opacity-40 cursor-pointer"
              onClick={(e) => e.stopPropagation()}
            >
              Terms of Service
            </a>{' '}
            and{' '}
            <a
              href="https://cavela.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:opacity-40 cursor-pointer"
              onClick={(e) => e.stopPropagation()}
            >
              Privacy Policy
            </a>
          </div>
        </div>

        <div className="relative">
          <ButtonV2
            id="register-cta-button"
            data-testid="cta-button"
            isLoading={registerMutation.isPending}
            isDisabled={registerMutation.isPending || !agreed_to_terms}
            type="submit"
          >
            Sign up
          </ButtonV2>
          {!agreed_to_terms && (
            <Tooltip
              content={
                <div>
                  You need to agree to the{' '}
                  <a
                    href="https://cavela.com/tos"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline hover:text-blue-800"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Terms of Service
                  </a>{' '}
                  and{' '}
                  <a
                    href="https://cavela.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline hover:text-blue-800"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Privacy Policy
                  </a>{' '}
                  to sign up
                </div>
              }
              closeDelay={0}
              placement="top"
            >
              <div className="absolute inset-0" />
            </Tooltip>
          )}
        </div>
      </form>

      {error && (
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ ease: 'easeInOut', duration: 1 }}
        >
          <Alert hideIcon className="mt-6" color="danger" title={error} />
        </motion.div>
      )}

      <div className="mt-6 flex items-center gap-1">
        <Typography as="span" variant="body-1">
          Already have an account?
        </Typography>
        <Typography
          onClick={() => navigate(ROUTE_PATHS.LOGIN)}
          as="a"
          variant="body-1"
          weight="bold"
          className="text-primary hover:opacity-40 cursor-pointer"
        >
          Sign in
        </Typography>
      </div>
    </div>
  );
};

export default RegisterForm;
